import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import AddSeasonModal from "../season/AddSeasonModal";
import LeagueInvitePlayerModal from "./LeagueInvitePlayerModal";
import LeagueQRCodeModal from "./LeagueQRCodeModal";
import { FaEnvelope, FaQrcode } from "react-icons/fa";
import { Dropdown } from "react-bootstrap";

function LeagueDetailPage({ leagueId: propLeagueId, onBack, onSeasonSelect }) {
    const { leagueId: paramLeagueId } = useParams(); // If route param is present
    const leagueId = propLeagueId || paramLeagueId;   // Fallback to prop or param

    const [leagueDetails, setLeagueDetails] = useState(null);
    const [seasons, setSeasons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showQRModal, setShowQRModal] = useState(false);
    const [selectedLeague, setSelectedLeague] = useState(null);
    const [selectedSeason, setSelectedSeason] = useState(null);
    const [openSeason, setOpenSeason] = useState(null);

    const getOpenSeason = () => {
        if (!leagueDetails?.seasons) return undefined;

        const today = new Date();
        const openSeason = leagueDetails.seasons.find(season => {
            const startDate = new Date(season.registration_start_date);
            const deadline = new Date(season.registration_deadline);
            return season.registration_open && today >= startDate && today <= deadline;
        });

        setOpenSeason(openSeason);
        return openSeason;
    };

    const handleQRCodeClick = (league) => {
        if (openSeason) {
            setSelectedLeague(league);
            setSelectedSeason(openSeason);
            setShowQRModal(true);
        } else {
            // Show an alert if no open season is found
            alert('No open registration seasons found for this league.');
        }
    };

    useEffect(() => {
        const fetchLeagueDetails = axios.get(`/api/leagues/leagues/${leagueId}/`, {
            withCredentials: true,
        });

        const fetchSeasons = axios.get(`/api/leagues/seasons/?league=${leagueId}`, {
            withCredentials: true,
        });

        Promise.all([fetchLeagueDetails, fetchSeasons])
            .then(([leagueResponse, seasonsResponse]) => {
                setLeagueDetails(leagueResponse.data);
                setSeasons(seasonsResponse.data);
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
                setError("Failed to load league details or seasons.");
                setLoading(false);
            });
    }, [leagueId]);

    // Separate useEffect to handle openSeason updates
    useEffect(() => {
        if (leagueDetails) {
            getOpenSeason();
        }
    }, [leagueDetails]);

    const handleAddSeason = (newSeason) => {
        setSeasons((prevSeasons) => [...prevSeasons, newSeason]);
    };

    if (loading) {
        return <div className="container">Loading league details...</div>;
    }

    if (error) {
        return <div className="container text-danger">{error}</div>;
    }

    return (
        <>
            <div className="container">
                {/* Card START */}
                <div className="card mb-3 p-0">
                    <div
                        className="h-200px rounded-top"
                        style={{
                            backgroundImage: `url(${leagueDetails.banner || `${process.env.PUBLIC_URL}/images/kb_static/Kickball_League_Banner.png`})`,
                            backgroundPosition: "center",
                            backgroundSize: "cover",
                            backgroundRepeat: "no-repeat",
                        }}
                    ></div>

                    {/* Card body START */}
                    <div className="card-body py-0">
                        <div className="d-sm-flex align-items-start text-center text-sm-start">
                            <div>
                                <div className="avatar avatar-xxl mt-n5 mb-3">
                                    <img
                                        className="avatar-img rounded-circle border border-white border-3"
                                        src={
                                            leagueDetails.logo ||
                                            `${process.env.PUBLIC_URL}/images/kb_static/default_logo.png`
                                        }
                                        alt={`${leagueDetails.name} logo`}
                                        style={{ backgroundColor: "white" }}
                                    />
                                </div>
                            </div>
                            <div className="d-flex align-items-center justify-content-between pb-3">
                                <div className="flex-grow-1 text-center">
                                    <div className="row">
                                        <div className="col">
                                            <h3 className="mb-0">{leagueDetails.name}</h3>
                                        </div>
                                    </div>
                                    <p className="mb-0 text-muted">{leagueDetails.description}</p>
                                </div>
                                <div className="d-flex align-items-center">
                                    <Dropdown>
                                        <Dropdown.Toggle bsPrefix="dropdown-icon btn-sm" variant="light" id="profileAction2">
                                            <i className="bi bi-three-dots"></i>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item onClick={() => setShowInviteModal(true)}>
                                                <FaEnvelope className="me-1" /> Invite Players
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => handleQRCodeClick(leagueDetails)}>
                                                <FaQrcode className="me-1" /> QR Code
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Card body END */}
                </div>
                {/* Card END */}

                {/* Invite Players Modal */}
                <LeagueInvitePlayerModal
                    show={showInviteModal}
                    handleClose={() => setShowInviteModal(false)}
                    league={leagueDetails}
                    season={openSeason}
                />

                <LeagueQRCodeModal
                    show={showQRModal}
                    handleClose={() => setShowQRModal(false)}
                    league={leagueDetails}
                    season={openSeason}
                />

                {/* League Details */}
                <div className="card card-body mb-3">
                    <div className="row g-4">
                        <div className="col-12">
                            <h5>Location</h5>
                            <p className="small mb-0">{leagueDetails.location}</p>
                        </div>
                        <div className="col-sm-6 col-lg-4">
                            <h5>Seasons</h5>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Existing Seasons</h6>
                                <button
                                    className="btn btn-primary btn-sm"
                                    data-bs-toggle="modal"
                                    data-bs-target="#addSeasonModal"
                                >
                                    Add Season
                                </button>
                            </div>
                            <ul className="list-unstyled">
                                {seasons.map((season) => (
                                    <li key={season.id}>
                                        <strong>
                                            <Link
                                                to={`/league-manager/seasons/${season.id}/details`}
                                                className="text-primary"
                                            >
                                                {season.name}
                                            </Link>
                                        </strong>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            {/* AddSeasonModal */}
            <AddSeasonModal leagueId={leagueId} onSeasonAdded={handleAddSeason} />
        </>
    );
}

export default LeagueDetailPage;
